export interface DiscordDocChange {
  kind: 'source' | 'translation' | 'review'
  sectionId?: string
  cs: string
  en: string
}

export interface DiscordDocRevision {
  date: string
  previousSourceDate: string
  sourceDate: string
  changes: DiscordDocChange[]
}

export interface DiscordDocPage {
  path: string
  originalUrl: string
  title: string
  csTitle: string
  description: string
  category: string
  ourDate: string // Source edition covered by the translation, not the translation/edit date.
  sourceDate: string // Edition most recently compared with the translation.
  sourceHash: string // Fingerprint of that reviewed source content.
  checkedAt: string
  translationUpdatedAt: string
  effectiveDate?: string // Effective date of the translated source edition, where published.
  needsUpdate: boolean
  history: DiscordDocRevision[]
}

// Shared by the website and the Bun freshness endpoint. Keep one source/version record per document.
export const discordDocsPages: DiscordDocPage[] = [
  {
    path: '/discord/guidelines',
    originalUrl: 'https://discord.com/guidelines',
    ourDate: '2025-08-29',
    title: 'Community Guidelines',
    csTitle: 'Discord Community Guidelines',
    description: 'Oficiální pravidla a pokyny komunity Discord přeložené do češtiny',
    category: 'Pravidla',
    sourceDate: '2025-08-29',
    sourceHash: '68eb2d8f0235ad4d2800f84f673a214743e947f4ca5d79ac8bc25b70660ad523',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'U pravidel 6–10 doplněn rozsah ochrany dětí, sexuálního obsahu a výslovného souhlasu dospělých.',
            en: 'Updated rules 6–10 covering child safety, sexual content, and adults’ explicit consent.',
            sectionId: 'guideline-6',
          },
          {
            kind: 'translation',
            cs: 'Opravena věková hranice na „alespoň 18 let“; doplněno věkové omezení dalšího obsahu sdíleného pro sexuální uspokojení.',
            en: 'Corrected the Czech age threshold to include 18-year-olds and expanded the age-restriction wording.',
            sectionId: 'guideline-9',
          },
          {
            kind: 'source',
            cs: 'Pravidlo 12 už není omezené na skutečná média. Pravidlo 27 upřesňuje újmu druhým; v pravidle 16 odstraněn odstavec chybějící v originálu.',
            en: 'Rule 12 is no longer limited to real media. Rule 27 specifies harm to others; removed a paragraph absent from the source in rule 16.',
            sectionId: 'guideline-12',
          },
        ],
      },
    ],
    effectiveDate: '2025-09-29',
  },
  {
    path: '/discord/terms',
    originalUrl: 'https://discord.com/terms',
    ourDate: '2025-08-29',
    title: 'Terms of Service',
    csTitle: 'Podmínky služby Discord',
    description:
      'Český překlad podmínek služby Discord včetně licencí, odpovědnosti a řešení sporů',
    category: 'Právní dokumenty',
    sourceDate: '2025-08-29',
    sourceHash: '1a5b209d65cf74157653e867840e3330bef29d386ae2d57c290834289b8987b8',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'review',
            cs: 'Dokončen překlad všech 17 částí dodaného originálu z 29. srpna 2025, účinného od 29. září 2025. Zkontrolovány odstavce, seznamy a odkazy; odstraněno označení historické verze.',
            en: 'Completed all 17 sections of the supplied August 29, 2025 source, effective September 29, 2025. Checked paragraphs, lists, and links; removed the historical-edition label.',
          },
          {
            kind: 'source',
            cs: 'Části 2–3: doplněno ověřování věku včetně třetích stran, odpovědnost rodičů, herní integrace, personalizace a bezpečnostní nastavení podle věku či bydliště.',
            en: 'Sections 2–3: added age checks including third-party services, parental responsibility, game integrations, personalisation, and safety defaults based on age or residence.',
          },
          {
            kind: 'translation',
            cs: 'Části 5–13: aktualizována licence k obsahu, služby třetích stran a omezení používání. Opravena lhůta odvolání pro EU od oznámení rozhodnutí; odškodnění už není popsáno jen pro firemní uživatele.',
            en: 'Sections 5–13: updated the content licence, third-party services, and use restrictions. Corrected the EU appeal deadline to run from notification; indemnity is no longer described as business-only.',
          },
          {
            kind: 'translation',
            cs: 'Část 16: přeloženo všech 41 odstavců včetně Kanady, NAM a ADR Services, příslušnosti soudů v Delaware, roční lhůty, dávek po 100 návrzích a nového opt-out. Doplněna část 17 a sladěno rychlé shrnutí.',
            en: 'Section 16: translated all 41 paragraphs, including Canada, NAM and ADR Services, Delaware courts, the one-year limit, batches of 100 demands, and the updated opt-out. Completed section 17 and aligned the quick summary.',
          },
        ],
      },
    ],
    effectiveDate: '2025-09-29',
  },
  {
    path: '/discord/server-safety/age-restriction',
    originalUrl:
      'https://support.discord.com/hc/en-us/articles/360040724612-Why-is-Discord-asking-for-my-birthday',
    ourDate: '2021-09-30',
    title: 'Age Restriction',
    csTitle: 'Proč Discord žádá o datum narození?',
    description: 'Informace o věkových omezeních a minimálním věku pro používání Discord',
    category: 'Bezpečnost serveru',
    sourceDate: '2021-09-30',
    sourceHash: 'c9806aeeed970b27af6e54033afcfcfbbf5a119c1c6ca74056a1def7ef54d065',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2021-11-21',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2021-09-30',
        sourceDate: '2021-09-30',
        changes: [
          {
            kind: 'review',
            cs: 'Zkontrolováno všech 31 věkových hranic podle zemí. Oproti překladu nebyla nalezena obsahová změna.',
            en: 'Checked all 31 country-specific minimum ages. No content change from the translation was found.',
          },
          {
            kind: 'translation',
            cs: 'Opraveno datum vydání originálu na 30. září 2021. Datum úpravy překladu zůstává 21. listopadu 2021.',
            en: 'Corrected the source edition date to September 30, 2021. The translation edit date remains November 21, 2021.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/violent-extremism-policy-explainer',
    originalUrl: 'https://discord.com/safety/violent-extremism-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Violent Extremism Policy',
    csTitle: 'Politika násilného extremismu',
    description: 'Vysvětlení politiky Discord proti násilnému extremismu a terorismu',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: '3e2715f1e1e587d674276d7bd68f1c4dfd308de0db9710cd86a2d348ec913236',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Upřesněn rozsah násilného extremismu a extremistických organizací; doplněno nahlášení porušujících serverů.',
            en: 'Updated the scope of violent extremism and extremist organisations; added reporting violating servers.',
            sectionId: 'how-to-avoid',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/hateful-conduct-policy-explainer',
    originalUrl: 'https://discord.com/safety/hateful-conduct-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Hateful Conduct Policy',
    csTitle: 'Politika nenávistného chování',
    description: 'Pravidla Discord proti nenávistným projevům a diskriminaci',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: '1165c63637d1e86f968ee2e8f7b1ca18a945c78ab161be52be09295a499a5e6e',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Aktualizována definice nenávisti, rozsah symbolů i na aplikace a podmíněná povaha kontextových výjimek.',
            en: 'Updated the hate definition, included apps in the symbol policy, and clarified that contextual exceptions are discretionary.',
            sectionId: 'hate-speech',
          },
          {
            kind: 'translation',
            cs: 'Rozlišeny genderová identita a pohlaví. Doplněno opuštění a nahlášení porušujících serverů a zdroje pomoci.',
            en: 'Distinguished gender identity from sex in Czech. Added reporting and leaving violating servers, and support resources.',
            sectionId: 'how-to-avoid',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/suicide-self-harm-policy-explainer',
    originalUrl: 'https://discord.com/safety/suicide-self-harm-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Suicide & Self-Harm Policy',
    csTitle: 'Politika sebevraždy a sebepoškozování',
    description: 'Ochrana duševního zdraví a prevence sebepoškozování na Discordu',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: 'ece6e0cae404da581ee5bc00a7b525fa1232f01b145bba2ca43bec5c7b41c1a6',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Doplněna samostatná část o poruchách příjmu potravy a aktualizováno doporučení pro porušující servery.',
            en: 'Added a dedicated eating-disorders section and updated guidance about violating servers.',
            sectionId: 'eating-disorders',
          },
          {
            kind: 'translation',
            cs: 'Rozšířeny odkazy na pomoc; odkaz ANAD vede přímo na ANAD. Redakční poznámka odděluje naši opravu kontaktů od textu Discordu.',
            en: 'Expanded support links and directed ANAD to its own website. An editorial note distinguishes our contact correction from Discord’s text.',
            sectionId: 'resources',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/platform-manipulation-policy-explainer',
    originalUrl: 'https://discord.com/safety/platform-manipulation-policy-explainer',
    ourDate: '2024-03-15',
    title: 'Platform Manipulation Policy',
    csTitle: 'Politika manipulace s platformou',
    description: 'Pravidla proti spamu, botům a zneužívání Discord platformy',
    category: 'Bezpečnost',
    sourceDate: '2024-03-15',
    sourceHash: 'a66bda88985055987061255c26343d637439f880dc20487fb6b03eb29ae11e27',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2024-03-15',
        changes: [
          {
            kind: 'source',
            cs: 'Doplněno umělé plnění Discord Questů a další předstíraná aktivita. Originál tyto změny obsahuje, přestože dál uvádí 15. březen 2024.',
            en: 'Added simulated Discord Quest completion and other fabricated activity. These changes appear in the source despite its unchanged March 15, 2024 date.',
            sectionId: 'inauthentic-engagement',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/deceptive-practices-policy-explainer',
    originalUrl: 'https://discord.com/safety/deceptive-practices-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Deceptive Practices Policy',
    csTitle: 'Politika klamavých praktik',
    description: 'Ochrana proti podvodům, phishingu a škodlivému chování',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: '39d91b6fba6d18c209759ae651b40414c4d2f52a8d248666d2a70371283134c9',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Doplněny padělky mezi příklady podvodů, nahlášení porušujících serverů a odkaz na běžné podvody.',
            en: 'Added counterfeit goods to fraud examples, reporting violating servers, and the common-scams resource.',
            sectionId: 'how-to-avoid',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/sexual-solicitation-policy-explainer',
    originalUrl: 'https://discord.com/safety/sexual-solicitation-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Sexual Solicitation Policy',
    csTitle: 'Politika sexuálního navádění',
    description: 'Pravidla proti nabízení a žádání o sexuální služby za úplatu',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: 'e5c102589a88baa937b2f77eafeda5eee1171bb93f5f638625a52923ded2e599',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Upřesněno, že tato politika řeší placené osobní sexuální služby. Opraveno příliš široké shrnutí a doplněn kontext obsahu pro dospělé.',
            en: 'Clarified that this policy covers paid in-person sexual services. Corrected the overly broad summary and added adult-content context.',
            sectionId: 'sexual-solicitation',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/identity-authenticity-policy-explainer',
    originalUrl: 'https://discord.com/safety/identity-authenticity-policy-explainer',
    ourDate: '2025-08-29',
    title: 'Identity & Authenticity Policy',
    csTitle: 'Politika identity a autenticity',
    description: 'Pravidla proti falešným profilům a obcházení banů',
    category: 'Bezpečnost',
    sourceDate: '2025-08-29',
    sourceHash: 'a566c149c9c821141abf72b013e976078b607fde0796ca69b9b9f1a7923c3d5f',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2024-03-15',
        sourceDate: '2025-08-29',
        changes: [
          {
            kind: 'source',
            cs: 'Odstraněna stará podmínka spojující zásah proti falešné identitě s dalším porušením pravidel. Upraven odkaz na odvolání.',
            en: 'Removed the old qualification tying identity enforcement to another policy violation. Updated the appeals link.',
            sectionId: 'identity-misrepresentation',
          },
          {
            kind: 'source',
            cs: 'Odstraněny podrobné příklady, které už aktuální originál neobsahuje. Jejich odstranění neznamená, že popsané jednání je povolené.',
            en: 'Removed detailed examples no longer present in the current source. Their removal does not mean the described conduct is allowed.',
          },
        ],
      },
    ],
  },
  {
    path: '/discord/safety/reporting-abusive-behavior-to-discord',
    originalUrl: 'https://discord.com/safety/360044103651-reporting-abusive-behavior-to-discord',
    ourDate: '2026-01-22',
    title: 'Reporting Content or Behavior',
    csTitle: 'Nahlašování obsahu a chování',
    description: 'Jak nahlásit porušení pravidel a nevhodné chování na Discordu',
    category: 'Bezpečnost',
    sourceDate: '2026-01-22',
    sourceHash: '8fcb13e6fb4072898148931c0da4c8f2871da745edba5bd40d410b46ea6b75ad',
    checkedAt: '2026-09-06',
    translationUpdatedAt: '2026-09-06',
    needsUpdate: false,
    history: [
      {
        date: '2026-09-06',
        previousSourceDate: '2025-07-03',
        sourceDate: '2026-01-22',
        changes: [
          {
            kind: 'translation',
            cs: 'Opraven odkaz na původní návod. Starý odkaz podpory už vede na jiný článek. Datum zdroje vychází z poslední redakční aktualizace.',
            en: 'Corrected the source link: the old support URL now leads to a different article. The source date uses the latest editorial update.',
          },
          {
            kind: 'source',
            cs: 'Doplněno nahlašování aplikací a upřesněno potvrzení e-mailem u hlášení podle DSA.',
            en: 'Added application reporting and clarified email confirmation for DSA reports.',
            sectionId: 'reporting-applications',
          },
          {
            kind: 'translation',
            cs: 'Shrnutí nyní rozlišuje cílové lhůty od záruk a upozorňuje na zákonné výjimky z důvěrnosti hlášení.',
            en: 'The summary now distinguishes response targets from guarantees and notes legal exceptions to report confidentiality.',
            sectionId: 'after-report',
          },
        ],
      },
    ],
  },
]

export function getDocPageByPath(path: string): DiscordDocPage | undefined {
  return discordDocsPages.find((page) => page.path === path)
}

export function formatDiscordDate(date: string, language: 'cs' | 'en' = 'cs'): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
